/**
 * Artwork and play affordances for Your Collections: the collection mosaic (2×2 covers, missing tiles filled with
 * gradient tiles seeded by the collection's name; one cover → full bleed), the "All sounds" artwork (ink → purple with a
 * soft neon glow and a big note, like Spotify's Liked Songs), the round neon-green play button and the "now playing"
 * equalizer bars.
 */
import { Music, Music2, Pause, Play } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import { genreTileColors } from './coverColors';

/** Three bars bouncing — "this is playing". Static (no motion) for reduced-motion users. */
export function Equalizer({ className, barClassName = 'bg-[#be43ff]' }: { className?: string; barClassName?: string }) {
  return (
    <span data-equalizer="true" aria-hidden className={cn('inline-flex h-3.5 items-end gap-[2px]', className)}>
      {[0, 0.25, 0.5].map((delay) => (
        <span
          key={delay}
          className={cn('h-full w-[3px] origin-bottom rounded-full motion-safe:animate-[audio-eq_0.9s_ease-in-out_infinite]', barClassName)}
          style={{ animationDelay: `${-delay}s`, transform: 'scaleY(0.6)' }}
        />
      ))}
    </span>
  );
}

/** The big round neon-green play / pause (48px on headers, 40px on cards). */
export function NeonPlayButton({ playing, label, onClick, size = 'md', className, disabled }: {
  playing: boolean;
  /** Accessible name, e.g. "Play Summer drop". */
  label: string;
  onClick: () => void;
  size?: 'md' | 'lg';
  className?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      aria-label={label}
      disabled={disabled}
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-[#a9f600] text-[#2e024c] shadow-[0_6px_16px_rgba(46,2,76,0.22)] outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-[#2e024c]/40 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100',
        size === 'lg' ? 'h-12 w-12' : 'h-10 w-10',
        className,
      )}
    >
      {playing
        ? <Pause className={size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'} fill="currentColor" aria-hidden />
        : <Play className={cn('ml-0.5', size === 'lg' ? 'h-5 w-5' : 'h-4 w-4')} fill="currentColor" aria-hidden />}
    </button>
  );
}

/** A calm gradient tile with a centred note (a missing cover in a mosaic). */
function SeedTile({ seed, big = false }: { seed: string; big?: boolean }) {
  const c = genreTileColors(seed);
  return (
    <span data-placeholder-cover="true" className="flex h-full w-full items-center justify-center" style={{ backgroundImage: `linear-gradient(135deg, ${c.from}, ${c.to})`, color: c.ink }}>
      <Music2 className={big ? 'h-1/4 w-1/4 opacity-60' : 'h-1/3 w-1/3 opacity-50'} aria-hidden />
    </span>
  );
}

/** Square artwork for a collection; sized by the parent (`h-full w-full`). */
export function CollectionArtwork({ name, covers, className }: { name: string; covers: string[]; className?: string }) {
  const cls = cn('h-full w-full overflow-hidden bg-[#f2f4f7]', className);
  if (covers.length <= 1) {
    return (
      <span data-artwork="collection" aria-hidden className={cn(cls, 'block')}>
        {covers[0] ? <img src={covers[0]} alt="" className="h-full w-full object-cover" loading="lazy" /> : <SeedTile seed={name} big />}
      </span>
    );
  }
  return (
    <span data-artwork="collection" aria-hidden className={cn(cls, 'grid grid-cols-2 grid-rows-2')}>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="block min-h-0 min-w-0 overflow-hidden">
          {covers[i] ? <img src={covers[i]} alt="" className="h-full w-full object-cover" loading="lazy" /> : <SeedTile seed={`${name} ${i}`} />}
        </span>
      ))}
    </span>
  );
}

/** The "All sounds" artwork: ink → purple, a soft neon glow, a big note. */
export function AllSoundsArtwork({ className }: { className?: string }) {
  return (
    <span data-artwork="all" aria-hidden className={cn('relative flex h-full w-full items-center justify-center overflow-hidden bg-gradient-to-br from-[#2e024c] to-[#be43ff]', className)}>
      <span className="pointer-events-none absolute -bottom-1/4 -right-1/4 h-3/4 w-3/4 rounded-full bg-[#a9f600] opacity-25 blur-2xl" />
      <Music className="relative h-[38%] w-[38%] text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.25)]" strokeWidth={2.2} aria-hidden />
    </span>
  );
}
